import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  DatePicker,
  Form,
  Popconfirm,
  Space,
  Statistic,
  Typography,
} from 'antd';
import { CalendarOutlined, HistoryOutlined, SyncOutlined } from '@ant-design/icons';
import { useT } from '../../../locales';
import dayjs from 'dayjs';
import { useCobroGeneracion, useGenerarCobros, useRecuperarCobros } from '../api/useCobros';
import { getCobroGeneracionErrorCode } from '../api/generacion-error';
import { parseGeneracionSkippedClients } from '../lib/generacion-skipped-clients';
import type { GenerarCobrosResult } from '@aid/shared';
import { COBRO_GENERACION_ERROR_CODE, COBRO_GENERACION_ESTADO } from '@aid/shared';

interface GenerarCobrosFormProps {
  onSuccess?: (generated: number) => void;
}

export function GenerarCobrosForm({ onSuccess }: GenerarCobrosFormProps) {
  const t = useT();
  const [form] = Form.useForm();
  const generar = useGenerarCobros();
  const recuperar = useRecuperarCobros();
  const [result, setResult] = useState<GenerarCobrosResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [executionId, setExecutionId] = useState<number | null>(null);
  const executionQuery = useCobroGeneracion(executionId ?? 0);
  const isPending = generar.isPending || recuperar.isPending;

  const applyResult = (nextResult: GenerarCobrosResult) => {
    setResult(nextResult);
    setExecutionId(nextResult.executionId ?? null);
    onSuccess?.(nextResult.generated);
  };

  const errorMessage = (nextError: unknown): string => {
    const code = getCobroGeneracionErrorCode(nextError);
    if (code) {
      return code === COBRO_GENERACION_ERROR_CODE.EN_PROGRESO
        ? t('pages.cobros.generarConflict')
        : t('pages.cobros.generarError');
    }
    return t('pages.cobros.generarError');
  };

  const handleRecovery = async () => {
    setError(null);
    setResult(null);
    setExecutionId(null);
    try {
      applyResult(await recuperar.mutateAsync());
    } catch (nextError: unknown) {
      setError(errorMessage(nextError));
    }
  };

  const handleFinish = async (values: Record<string, unknown>) => {
    setError(null);
    setResult(null);
    setExecutionId(null);
    try {
      const fechaCobro = values.fechaCobro as dayjs.Dayjs;
      applyResult(await generar.mutateAsync({ fechaCobro: fechaCobro.format('YYYY-MM-DD') }));
    } catch (nextError: unknown) {
      setError(errorMessage(nextError));
    }
  };

  const terminalExecution = executionQuery.data;
  const visibleResult = terminalExecution
    ? {
        ...result,
        generated: terminalExecution.counters.generated,
        skipped: terminalExecution.counters.skipped,
        failed: terminalExecution.counters.failed,
        creditoAplicado: terminalExecution.counters.creditoAplicado,
      }
    : result;
  const failed = visibleResult?.failed ?? 0;
  // S1/T34: per-client skip reasons come from the persisted run detail. Older
  // runs have no `skippedClients` key and simply render no list.
  const skippedClients = parseGeneracionSkippedClients(terminalExecution?.metadata);

  return (
    <div>
      <Card
        title={t('pages.cobros.recuperarTitle')}
        style={{ marginBottom: 16 }}
        styles={{ body: { paddingTop: 4 } }}
      >
        <Typography.Paragraph type="secondary" style={{ maxWidth: 720 }}>
          {t('pages.cobros.recuperarDescription')}
        </Typography.Paragraph>
        <Popconfirm
          title={t('pages.cobros.recuperarConfirmTitle')}
          description={t('pages.cobros.recuperarConfirmDescription')}
          okText={t('pages.cobros.recuperarConfirmOk')}
          cancelText={t('common.cancel')}
          onConfirm={handleRecovery}
        >
          <Button type="primary" icon={<SyncOutlined />} loading={isPending}>
            {t('pages.cobros.recuperarBtn')}
          </Button>
        </Popconfirm>
      </Card>

      <Collapse
        defaultActiveKey={['exact-date']}
        items={[
          {
            key: 'exact-date',
            label: t('pages.cobros.generarAdvancedTitle'),
            children: (
              <div>
                <Typography.Paragraph type="secondary" style={{ maxWidth: 720 }}>
                  {t('pages.cobros.generarAdvancedDescription')}
                </Typography.Paragraph>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFinish}
                  style={{ maxWidth: 400, width: '100%' }}
                >
                  <Form.Item
                    name="fechaCobro"
                    label={t('pages.cobros.fechaCobro')}
                    rules={[{ required: true, message: t('pages.cobros.fechaCobroRequired') }]}
                  >
                    <DatePicker style={{ width: '100%' }} suffixIcon={<CalendarOutlined />} />
                  </Form.Item>
                  <Form.Item>
                    <Space wrap size={[8, 8]}>
                      <Button
                        type="default"
                        htmlType="submit"
                        icon={<HistoryOutlined />}
                        loading={isPending}
                      >
                        {t('pages.cobros.generarBtn')}
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </div>
            ),
          },
        ]}
      />

      {executionQuery.data?.estado === COBRO_GENERACION_ESTADO.EN_PROGRESO && (
        <Alert
          type="info"
          showIcon
          message={t('pages.cobros.generarInProgress')}
          style={{ marginTop: 16 }}
        />
      )}
      {visibleResult !== null && (
        <Alert
          type={failed > 0 ? 'warning' : 'success'}
          showIcon
          message={
            failed > 0 ? t('pages.cobros.generarPartialSuccess') : t('pages.cobros.generarSuccess')
          }
          description={
            <div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                  gap: 16,
                }}
              >
                <Statistic
                  title={t('pages.cobros.generarTitle')}
                  value={visibleResult.generated}
                  suffix={t('pages.cobros.generarSuffix')}
                />
                <Statistic
                  title={t('pages.cobros.generarSkipped')}
                  value={visibleResult.skipped ?? 0}
                />
                <Statistic
                  title={t('pages.cobros.generarFailed')}
                  value={visibleResult.failed ?? 0}
                />
                <Statistic
                  title={t('pages.cobros.generarCredit')}
                  value={visibleResult.creditoAplicado ?? '0.00'}
                />
              </div>
              {skippedClients.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Typography.Text strong>
                    {t('pages.cobros.generarSkippedClientsTitle')}
                  </Typography.Text>
                  <ul style={{ margin: '4px 0 0', paddingInlineStart: 20 }}>
                    {skippedClients.map((skip) => (
                      <li key={`${skip.idCliente}-${skip.code}`}>
                        {t('pages.cobros.generarSkippedClient', { id: skip.idCliente })}:{' '}
                        {skip.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          }
          style={{ marginTop: 16 }}
        />
      )}
      {error && (
        <Alert type="error" showIcon message={error} style={{ marginTop: 16, maxWidth: 720 }} />
      )}
    </div>
  );
}
